import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../Environment';
import useRetryFetch from '../../../hooks/useRetryFetch';
import LoggerContext from '../../../context/LoggerProvider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { toast } from 'sonner';
import { BarChart3, Users, TrendingUp, Loader2, GitBranch } from 'lucide-react';
import { listPwfs, type Pwf } from '../../../api/pwf';

interface GroupReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  collectionName: string;
  collectionLevel: number;
}

interface ActivityEvent {
  eventId: string;
  userId: string;
  userEmail: string;
  userName: string;
  workflowId?: string;
  workflowName?: string;
  processId: string;
  activity: string;
  eventData: Record<string, any>; // THE ACTUAL DATA!
  created: string;
}

interface ReportData {
  // Activity Summary response
  totalEvents?: number;
  totalUsers?: number;
  userSummary?: Array<{
    userId: string;
    userEmail: string;
    userName: string;
    totalEvents: number;
    activities: Record<string, number>;
  }>;
  eventsByActivity?: Record<string, ActivityEvent[]>;
  allEvents?: ActivityEvent[];

  // Student Progress response
  students?: Array<{
    user: {
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      displayName?: string;
    };
    subcollection?: {
      id: string;
      name: string;
    };
    processes: any[];
    totalProcesses: number;
    completedProcesses: number;
    activeProcesses: number;
  }>;
  totalStudents?: number;

  // PWF Progress response
  pwfStudents?: Array<{
    user: {
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      displayName?: string;
    };
    pwfInstanceId: string | null;
    title: string | null;
    lifecycleState: string;
    lastActivityAt: string | null;
    stalled: boolean;
  }>;
}

const PWF_LIFECYCLE_LABEL: Record<string, string> = {
  not_started: 'Not started',
  draft: 'Draft',
  pending: 'Pending',
  active: 'Active',
  in_progress: 'In Progress',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
};

const GroupReportModal: React.FC<GroupReportModalProps> = ({
  open,
  onOpenChange,
  collectionId,
  collectionName,
  collectionLevel,
}) => {
  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const retryFetch = useRetryFetch();

  const [activeTab, setActiveTab] = useState('activity');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [templates, setTemplates] = useState<Pwf[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const getLevelLabel = (level: number) => {
    if (level === 10) return t('department');
    if (level === 20) return t('course');
    if (level === 30) return t('class');
    return `${t('level')} ${level}`;
  };

  const fetchReport = async (reportType: 'activity' | 'student' | 'pwf') => {
    if (!collectionId) return;
    if (reportType === 'pwf' && !selectedTemplateId) return;

    try {
      setLoading(true);
      let url = '';

      if (reportType === 'activity') {
        url = `${config.API_BASE}/api/collection/${collectionId}/report/activity-summary`;
        if (dateRange.start && dateRange.end) {
          url += `?startDate=${dateRange.start}&endDate=${dateRange.end}`;
        }
      } else if (reportType === 'pwf') {
        url = `${config.API_BASE}/api/collection/${collectionId}/report/pwf-progress?templateId=${selectedTemplateId}`;
      } else {
        url = `${config.API_BASE}/api/collection/${collectionId}/report/student-progress`;
      }

      const response = await retryFetch(url);

      if (response.ok) {
        const data = await response.json();
        if (reportType === 'pwf') {
          setReportData((prev) => ({ ...prev, pwfStudents: data.students }));
        } else {
          setReportData(data);
        }
      } else {
        toast.error(t('failed_to_generate_report'));
        if (reportType !== 'pwf') setReportData(null);
      }
    } catch (error) {
      Logger.error('Failed to generate report:', error);
      toast.error(t('failed_to_generate_report'));
      if (reportType !== 'pwf') setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // Load the mentor's own templates once, for the PWF Progress tab's picker
  useEffect(() => {
    if (!open) return;
    listPwfs(retryFetch, { isTemplate: true })
      .then(setTemplates)
      .catch((err) => Logger.error('Failed to load templates:', err));
  }, [open]);

  // Fetch report when tab changes
  useEffect(() => {
    if (open && collectionId && activeTab !== 'pwf') {
      fetchReport(activeTab as 'activity' | 'student');
    }
  }, [open, collectionId, activeTab]);

  // Refetch activity report when date range changes
  useEffect(() => {
    if (open && activeTab === 'activity' && (dateRange.start || dateRange.end)) {
      fetchReport('activity');
    }
  }, [dateRange.start, dateRange.end]);

  // Fetch PWF progress when the tab is active and a template is selected
  useEffect(() => {
    if (open && activeTab === 'pwf' && selectedTemplateId) {
      fetchReport('pwf');
    }
  }, [open, activeTab, selectedTemplateId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {collectionName} - {getLevelLabel(collectionLevel)}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="activity">
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('group_activity')}
            </TabsTrigger>
            <TabsTrigger value="student">
              <Users className="h-4 w-4 mr-2" />
              {t('student_progress')}
            </TabsTrigger>
            <TabsTrigger value="pwf">
              <GitBranch className="h-4 w-4 mr-2" />
              PWF Progress
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="space-y-4">
            {/* Date Range Filters */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('filters')}</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">{t('start_date')}</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">{t('end_date')}</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Activity Summary Results */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3">{t('loading')}</span>
              </div>
            ) : reportData?.totalEvents !== undefined && reportData.totalEvents > 0 ? (
              <>
                {/* Summary Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {t('total_events')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">
                        {reportData.totalEvents}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {t('users_with_events')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">
                        {reportData.userSummary?.length || 0}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Actual Event Data - Full Detail */}
                {reportData.allEvents && reportData.allEvents.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('event_data_detail')}</CardTitle>
                      <CardDescription>
                        {t('showing_actual_data_submitted_by_users')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4 max-h-[500px] overflow-y-auto">
                        {reportData.allEvents.map((event, idx) => (
                          <div
                            key={idx}
                            className="p-4 rounded-lg border-2 hover:border-primary/50 transition-colors space-y-2"
                          >
                            {/* Event Header */}
                            <div className="flex items-start justify-between flex-wrap gap-2 pb-2 border-b">
                              <div>
                                <div className="font-semibold text-sm">{event.userName}</div>
                                <div className="text-xs text-muted-foreground">
                                  {event.userEmail}
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge variant="outline">{event.activity}</Badge>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(event.created).toLocaleString()}
                                </div>
                              </div>
                            </div>

                            {/* Workflow Info */}
                            {event.workflowName && (
                              <div className="text-xs text-muted-foreground">
                                {t('workflow')}: {event.workflowName}
                              </div>
                            )}

                            {/* THE ACTUAL EVENT DATA! */}
                            <div className="bg-muted/30 p-3 rounded space-y-1">
                              <div className="text-xs font-semibold text-muted-foreground mb-2">
                                {t('submitted_data')}:
                              </div>
                              {Object.entries(event.eventData).map(([key, value]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium">{key}:</span>{' '}
                                  <span className="text-muted-foreground">
                                    {typeof value === 'object'
                                      ? JSON.stringify(value, null, 2)
                                      : String(value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <BarChart3 className="mx-auto h-12 w-12 mb-2 opacity-50" />
                <p>{t('no_data_for_report')}</p>
                <p className="text-sm mt-2">
                  {t('activity_events_require_dataevent_components')}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="student" className="space-y-4">
            {/* Student Progress Results */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3">{t('loading')}</span>
              </div>
            ) : reportData?.students && reportData.students.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {t('total_students')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{reportData.totalStudents || 0}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {t('total_processes')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {reportData.students.reduce((acc, s) => acc + s.totalProcesses, 0)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {t('completed_processes')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        {reportData.students.reduce((acc, s) => acc + s.completedProcesses, 0)}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Student Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('student_progress')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('student')}</TableHead>
                            <TableHead>{t('email')}</TableHead>
                            <TableHead className="text-center">{t('total')}</TableHead>
                            <TableHead className="text-center">{t('active')}</TableHead>
                            <TableHead className="text-center">{t('completed')}</TableHead>
                            <TableHead>{t('sub_collection')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportData.students.map((student, idx: number) => {
                            const displayName =
                              student.user.displayName ||
                              [student.user.firstName, student.user.lastName]
                                .filter(Boolean)
                                .join(' ') ||
                              student.user.email.split('@')[0];

                            return (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{displayName}</TableCell>
                                <TableCell className="text-muted-foreground">
                                  {student.user.email}
                                </TableCell>
                                <TableCell className="text-center font-semibold">
                                  {student.totalProcesses}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="secondary">{student.activeProcesses}</Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="default">{student.completedProcesses}</Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {student.subcollection?.name || '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Users className="mx-auto h-12 w-12 mb-2 opacity-50" />
                <p>{t('no_data_for_report')}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="pwf" className="space-y-4">
            {/* Template Picker */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Template</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((tpl) => (
                      <SelectItem key={tpl._id} value={tpl._id}>
                        {tpl.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {!selectedTemplateId ? (
              <div className="text-center text-muted-foreground py-8">
                <GitBranch className="mx-auto h-12 w-12 mb-2 opacity-50" />
                <p>Select a template to see the class roster</p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3">{t('loading')}</span>
              </div>
            ) : reportData?.pwfStudents && reportData.pwfStudents.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {t('total_students')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{reportData.pwfStudents.length}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Not Started
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {
                          reportData.pwfStudents.filter(
                            (s) => s.lifecycleState === 'not_started'
                          ).length
                        }
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Stalled
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-amber-600">
                        {reportData.pwfStudents.filter((s) => s.stalled).length}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Student Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>PWF Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('student')}</TableHead>
                            <TableHead>{t('email')}</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Last Active</TableHead>
                            <TableHead className="text-center">Stalled</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportData.pwfStudents.map((student, idx: number) => {
                            const displayName =
                              student.user.displayName ||
                              [student.user.firstName, student.user.lastName]
                                .filter(Boolean)
                                .join(' ') ||
                              student.user.email.split('@')[0];

                            return (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{displayName}</TableCell>
                                <TableCell className="text-muted-foreground">
                                  {student.user.email}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      student.lifecycleState === 'not_started'
                                        ? 'outline'
                                        : student.lifecycleState === 'completed'
                                        ? 'default'
                                        : 'secondary'
                                    }
                                  >
                                    {PWF_LIFECYCLE_LABEL[student.lifecycleState] ??
                                      student.lifecycleState}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {student.lastActivityAt
                                    ? new Date(student.lastActivityAt).toLocaleDateString()
                                    : '-'}
                                </TableCell>
                                <TableCell className="text-center">
                                  {student.stalled && (
                                    <Badge variant="destructive">Stalled</Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Users className="mx-auto h-12 w-12 mb-2 opacity-50" />
                <p>{t('no_data_for_report')}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default GroupReportModal;
